// Estados brasileiros: lista canônica e normalização tolerante.
//
// A forma canônica são os 27 nomes por extenso. Não é escolha nossa: é a lista
// que o Kateto devolve na mensagem de erro quando recusa uma pasta, e é o que
// 1.101 dos 1.183 registros preenchidos já usam.
//
// ⚠️ ESTE ALGORITMO ESTÁ DUPLICADO no nó "Normalizar campos" do fluxo n8n
// "Envio para Jurídico - Kateto". Se mudar aqui, mude lá — front e integração
// divergirem significa a tela mostrar um valor e o parceiro receber outro.

export const ESTADOS_BR = [
  'Acre', 'Alagoas', 'Amapá', 'Amazonas', 'Bahia', 'Ceará', 'Distrito Federal',
  'Espírito Santo', 'Goiás', 'Maranhão', 'Mato Grosso', 'Mato Grosso do Sul',
  'Minas Gerais', 'Pará', 'Paraíba', 'Paraná', 'Pernambuco', 'Piauí',
  'Rio de Janeiro', 'Rio Grande do Norte', 'Rio Grande do Sul', 'Rondônia',
  'Roraima', 'Santa Catarina', 'São Paulo', 'Sergipe', 'Tocantins',
] as const;

export const SIGLAS_BR: Record<string, string> = {
  AC: 'Acre', AL: 'Alagoas', AP: 'Amapá', AM: 'Amazonas', BA: 'Bahia', CE: 'Ceará',
  DF: 'Distrito Federal', ES: 'Espírito Santo', GO: 'Goiás', MA: 'Maranhão',
  MT: 'Mato Grosso', MS: 'Mato Grosso do Sul', MG: 'Minas Gerais', PA: 'Pará',
  PB: 'Paraíba', PR: 'Paraná', PE: 'Pernambuco', PI: 'Piauí', RJ: 'Rio de Janeiro',
  RN: 'Rio Grande do Norte', RS: 'Rio Grande do Sul', RO: 'Rondônia', RR: 'Roraima',
  SC: 'Santa Catarina', SP: 'São Paulo', SE: 'Sergipe', TO: 'Tocantins',
};

/** Tira acento e caixa, só para COMPARAR — nunca para gravar. */
function dobra(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Apara pontas, colapsa espaços e mata tabulação de colagem de planilha. */
function limpo(v: unknown): string {
  return String(v ?? '').replace(/\s+/g, ' ').trim();
}

const POR_DOBRA = new Map(ESTADOS_BR.map((e) => [dobra(e), e as string]));

/**
 * Devolve um dos 27 nomes oficiais, ou o valor original quando não reconhece.
 *
 * Nunca chuta: cidade no lugar do estado ("Recife") e erro de digitação
 * ("santa cantarina") voltam intactos. Adivinhar aqui erraria em silêncio, e um
 * acerto silencioso tem a mesma cara de um erro silencioso.
 */
export function normalizeEstado(valor: unknown): string {
  const original = limpo(valor);
  if (!original) return '';

  // 1) já é um dos 27, exatamente
  if ((ESTADOS_BR as readonly string[]).includes(original)) return original;

  // 2) sufixo de sigla: 'São Paulo (SP)', 'São Paulo- SP', 'Minas Gerais / MG'
  const semSufixo = limpo(original.replace(/[(\-/,]\s*[A-Za-z]{2}\s*\)?\s*$/, ''));
  if (semSufixo) {
    const achado = POR_DOBRA.get(dobra(semSufixo));
    if (achado) return achado;
  }

  // 3) só caixa e/ou acento: 'SÃO PAULO', 'Espirito Santo', 'PARANA'
  const porDobra = POR_DOBRA.get(dobra(original));
  if (porDobra) return porDobra;

  // 4) sigla, tolerando pontuação: 'SP', 'R/S', 'S.P.'
  const letras = original.replace(/[^A-Za-z]/g, '').toUpperCase();
  if (letras.length === 2 && SIGLAS_BR[letras]) return SIGLAS_BR[letras];

  // 5) não reconheceu — devolve intacto, de propósito
  return original;
}

/** true quando o valor (após normalizar) é um dos 27. */
export function isEstadoCanonico(valor: unknown): boolean {
  const n = normalizeEstado(valor);
  return !!n && (ESTADOS_BR as readonly string[]).includes(n);
}

/**
 * Opções para o select, já contando o valor atual.
 *
 * Se o valor guardado não for canônico, ele entra como opção extra. Sem isso o
 * SearchableSelect não encontraria correspondência e mostraria o placeholder —
 * o campo pareceria VAZIO, e salvar o negócio apagaria o dado. Foi o motivo de
 * não virarmos `select` puro: 81 registros iriam parecer vazios.
 */
export function opcoesEstado(valorAtual: unknown): { value: string; label: string }[] {
  const base = ESTADOS_BR.map((e) => ({ value: e as string, label: e as string }));
  const atual = limpo(valorAtual);
  if (!atual) return base;

  const normalizado = normalizeEstado(atual);
  if ((ESTADOS_BR as readonly string[]).includes(normalizado)) return base;

  // Fora da lista: preserva e sinaliza, em vez de sumir.
  return [{ value: atual, label: `${atual} (fora da lista)` }, ...base];
}
